/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description Configure message for building
 */

import type {
  InitialMessageData,
  Listable,
  MessageDataForBuilding,
  Property,
} from '@zcode/state-type'
import type { Mutable } from '@zcode/utility-types'
import { logger } from '../../../../logger'

const configureForBuilding = (
  messageData: Mutable<InitialMessageData>,
  building: (Listable | Property) & { isBuilding: true },
  selectedFloorplanId: number | null | undefined
): asserts messageData is Mutable<MessageDataForBuilding> => {
  if (!building) {
    return
  }
  // use the first active/inactive floor plan if there is no selected floor plan
  // @ts-expect-error `messageData.selectedFloorplanId` isn't a thing. I'm pretty sure we never include it in messageData, but not 100% sure
  let floorplanId = selectedFloorplanId || messageData.selectedFloorplanId
  const hasActiveFloorplans = building.activeFloorplans && !!building.activeFloorplans.length
  if (!floorplanId && !messageData.listingIds && hasActiveFloorplans) {
    floorplanId = building.activeFloorplans[0].listingId
  }
  // adding a logger to determine why activeFloorplanCount and floorplanCount are sometimes unreliable
  // https://github.com/zumper/zumper_code/issues/18251
  if (
    building.activeFloorplans &&
    building.activeFloorplanCount !== building.activeFloorplans.length
  ) {
    logger.error('Inconsistent activeFloorplanCount in configureForBuilding', building)
  }

  if (building.floorplans && building.floorplanCount !== building.floorplans.length) {
    logger.error('Inconsistent floorplanCount in configureForBuilding', building)
  }

  const shouldUseBuildingId = !floorplanId || !hasActiveFloorplans

  const extras: Pick<
    Mutable<MessageDataForBuilding>,
    'building' | 'buildingIds' | 'listingIds' | 'messagedBuildingIds'
  > = {
    building,
    buildingIds: shouldUseBuildingId ? [building.buildingId] : undefined,
    listingIds: shouldUseBuildingId ? undefined : [floorplanId],
    messagedBuildingIds: [building.buildingId],
  }

  // listing config definition
  Object.assign(messageData, extras)
}

export default configureForBuilding
